import type { NextSeoProps } from "next-seo";
import { type FunctionComponent } from "react";
import { graphql, useFragment } from "react-relay";

import { PageSeo } from "scmp-app/components/meta";
import { useKeywordMetaTags } from "scmp-app/components/meta/hooks";
import { config } from "scmp-app/data";
import { useCanonicalUrl } from "scmp-app/lib/hooks";
import type { authorSeoAuthor$key } from "scmp-app/queries/__generated__/authorSeoAuthor.graphql";

type Props = {
  extraSeoOptions?: NextSeoProps;
  reference: authorSeoAuthor$key;
};

export const AuthorSeo: FunctionComponent<Props> = ({
  extraSeoOptions = {},
  reference: reference_,
}) => {
  const author = useFragment(
    graphql`
      fragment authorSeoAuthor on Author {
        entityId
        bio
        name
        urlAlias
      }
    `,
    reference_,
  );

  const keywordTags = useKeywordMetaTags(author.name);
  const { canonicalUrl } = useCanonicalUrl(author.urlAlias);

  const sanitizedAuthorBio = author.bio ?? "";

  return (
    <PageSeo
      {...extraSeoOptions}
      additionalLinkTags={[
        {
          href: `${config.domain.scmp}/rss/${author.entityId}/feed`,
          rel: "alternate",
          type: "application/rss+xml",
        },
      ]}
      additionalMetaTags={[...keywordTags]}
      canonical={canonicalUrl}
      description={sanitizedAuthorBio}
      openGraph={{ description: sanitizedAuthorBio, url: canonicalUrl }}
      title={author.name}
    />
  );
};

AuthorSeo.displayName = "AuthorSeo";
